// src/utils/nameMatch.ts
//
// Shoda jména při dokončení registrace (utils/completeRegistration, nález 8
// 2. kola revize): importovaných profilů se stejným e-mailem bývá víc –
// rodina se společným e-mailem (otec a syn) nebo duplicitní import téže
// osoby. Automaticky se smí převést jen profil, jehož jméno odpovídá tomu,
// kdo se registruje; jinak se aplikace zeptá.
// 3. kolo (nález 1): generační přípona (ml., st., jr. …) musí sedět na obou
// stranách – „Jan Novák“ (otec) není „Jan Novák ml.“ (syn) – a stejná slova
// jsou lepší shoda než jméno obsažené v delším (nameMatchQuality).

import { stripDiacritics } from "../sepa";
import { toTitleCase } from "./nameUtils";

/**
 * Generační přípony (slova z nameWords) → skupina se stejným významem:
 * „ml.“ = „mladší“ = „jr.“ = „jun.“ = „junior“, obdobně starší a nej-.
 */
const GENERATIONS = new Map<string, string>([
  ["ml", "ml"],
  ["mladsi", "ml"],
  ["jr", "ml"],
  ["jun", "ml"],
  ["junior", "ml"],
  ["st", "st"],
  ["starsi", "st"],
  ["sr", "st"],
  ["sen", "st"],
  ["senior", "st"],
  ["nejml", "nejml"],
  ["nejmladsi", "nejml"],
  ["nejst", "nejst"],
  ["nejstarsi", "nejst"],
]);

/** Skupina generační přípony („ml“, „jr“ → "ml"), jiné slovo → null. */
const generationOf = (word: string): string | null => GENERATIONS.get(word) || null;

/**
 * Slova jména bez diakritiky, malými písmeny a bez interpunkce, bez
 * opakování. Slova odděluje mezera nebo pomlčka:
 * ("Jana", "Nováková-Svobodová ml.") → jana, novakova, svobodova, ml.
 * Hodnoty, které nejsou text (chybějící jméno v importu), se přeskočí.
 */
export function nameWords(...parts: unknown[]): string[] {
  const words: string[] = [];
  parts.forEach((part) => {
    if (typeof part !== "string") return;
    stripDiacritics(part)
      .toLowerCase()
      .split(/[\s‐-―-]+/)
      .map((word) => word.replace(/[^a-z0-9]/g, ""))
      .forEach((word) => {
        if (word && words.indexOf(word) === -1) words.push(word);
      });
  });
  return words;
}

/** Slova jména bez generačních přípon a seřazené skupiny přípon ("ml,st"). */
function splitGeneration(words: string[]): { base: string[]; generations: string } {
  const base: string[] = [];
  const generations: string[] = [];
  words.forEach((word) => {
    const generation = generationOf(word);
    if (generation === null) base.push(word);
    else if (generations.indexOf(generation) === -1) generations.push(generation);
  });
  return { base, generations: generations.sort().join(",") };
}

/**
 * Jak dobře sedí dvě jména (slova z nameWords)? Pořadí slov nerozhoduje.
 *  - 2 = stejná slova: „Jana Nováková“ = „Nováková Jana“;
 *  - 1 = jméno s méně slovy (aspoň 2) je celé obsažené v tom delším:
 *    „Jana Nováková“ v „Jana Nováková Svobodová“;
 *  - 0 = nesedí: „Petr Novák“ ≠ „Jan Novák“, samotné „Novák“ (1 slovo)
 *    se neshoduje s ničím.
 * Generační přípony se porovnávají zvlášť a musí být na obou stranách
 * stejné: „Jan Novák“ ≠ „Jan Novák ml.“, ale „Jan Novák ml.“ = „Novák Jan jr.“.
 */
export function nameMatchQuality(a: string[], b: string[]): 0 | 1 | 2 {
  const x = splitGeneration(a);
  const y = splitGeneration(b);
  if (x.generations !== y.generations) return 0;
  const [shorter, longer] = x.base.length <= y.base.length ? [x.base, y.base] : [y.base, x.base];
  if (shorter.length < 2) return 0;
  if (!shorter.every((word) => longer.indexOf(word) !== -1)) return 0;
  return shorter.length === longer.length ? 2 : 1;
}

/** Shodují se dvě jména (slova z nameWords)? Viz nameMatchQuality. */
export function namesMatch(a: string[], b: string[]): boolean {
  return nameMatchQuality(a, b) > 0;
}

/**
 * Jméno k zobrazení: neprázdné části spojené mezerou, slova s velkým
 * počátečním písmenem (toTitleCase), generační přípony malými písmeny –
 * „JAN“, „NOVÁK ML.“ → „Jan Novák ml.“ (dřív „Jan Novák Ml.“). Hodnoty,
 * které nejsou text, se přeskočí.
 */
export function formatPersonName(...parts: unknown[]): string {
  return parts
    .filter((part): part is string => typeof part === "string" && part.trim() !== "")
    .map((part) => toTitleCase(part))
    .join(" ")
    .split(" ")
    .map((token) => {
      const words = nameWords(token);
      return words.length === 1 && generationOf(words[0]) !== null ? token.toLowerCase() : token;
    })
    .join(" ");
}
